import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  fetchAtendimentosIniciados,
  setAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import { gruposSelector } from 'store/reducers/grupos';
import {
  fetchMensagensByAtendimento,
  fetchMensagensByGrupo,
  fetchMensagensFinalizadasByAtendimento,
  mensagensSelector,
  navegarParaMensagem,
  navegarParaMensagemGrupo,
  resetAtendimentoCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensAtendimento,
  resetMensagensFinalizadas,
  resetMensagensGrupos,
  setCursorAConsumir,
  setIdMensagemPesquisada,
} from 'store/reducers/mensagens';
import { getUrlParams } from 'utils/getUrlParams';

export function useNavigateToMessage() {
  const dispatch = useAppDispatch();
  const { atendimentoSelecionado, historicoAtendimentoSelecionado } =
    useAppSelector(atendimentosSelector);
  const { grupoSelecionado } = useAppSelector(gruposSelector);
  const { mensagemEstaMontada } = useAppSelector(mensagensSelector);

  const { orderBy } = getUrlParams();

  const [mensagemParaNavegar, setMensagemParaNavegar] = useState<{
    id: string;
    afterCursor: string;
  } | null>(null);

  const [mensagemHistorico, setMensagemHistorico] = useState<{
    id: string;
    afterCursor: string;
  } | null>(null);

  const [mensagemGrupo, setMensagemGrupo] = useState<{
    id: string;
    afterCursor: string;
  } | null>(null);

  const handleNavigateToMessage = (id: string, atendimentoId?: string) => {
    // Se precisa trocar de atendimento
    if (atendimentoId && atendimentoId !== atendimentoSelecionado?.id) {
      dispatch(resetAtendimentoCursorAConsumir());
      dispatch(setAtendimentoSelecionado());
      dispatch(fetchAtendimentosIniciados({ orderBy }))
        .unwrap()
        .then(atendimentos => {
          const atendimento = atendimentos.find(
            atendimento => atendimento.id === atendimentoId,
          );

          if (atendimento) {
            dispatch(setAtendimentoSelecionado(atendimento));

            setTimeout(() => {
              dispatch(navegarParaMensagem(id))
                .unwrap()
                .then(data => {
                  dispatch(resetIdMensagemPesquisada());
                  dispatch(setIdMensagemPesquisada(id));

                  setMensagemParaNavegar({
                    id,
                    afterCursor: data.data.afterCursor,
                  });
                });
            }, 1000);
          }
        });

      return;
    }

    // Se o atendimento já está selecionado ou não foi fornecido atendimentoId
    dispatch(navegarParaMensagem(id))
      .unwrap()
      .then(data => {
        if (!atendimentoSelecionado) return;

        dispatch(resetIdMensagemPesquisada());
        dispatch(setIdMensagemPesquisada(id));

        setMensagemParaNavegar({
          id,
          afterCursor: data.data.afterCursor,
        });
      });
  };

  const handleNavegarAteMensagemHistorico = (id: string) => {
    dispatch(navegarParaMensagem(id))
      .unwrap()
      .then(data => {
        if (!historicoAtendimentoSelecionado) return;

        dispatch(resetIdMensagemPesquisada());
        dispatch(setIdMensagemPesquisada(id));

        setMensagemHistorico({
          id,
          afterCursor: data.data.afterCursor,
        });
      });
  };

  const handleNavigateToGroupMessage = (id: string) => {
    dispatch(navegarParaMensagemGrupo(id))
      .unwrap()
      .then(data => {
        if (!grupoSelecionado) return;

        dispatch(resetIdMensagemPesquisada());
        dispatch(setIdMensagemPesquisada(id));

        setMensagemGrupo({
          id,
          afterCursor: data.data.afterCursor,
        });
      });
  };

  useEffect(() => {
    if (!mensagemParaNavegar || !atendimentoSelecionado) return;

    const { afterCursor } = mensagemParaNavegar;

    if (!mensagemEstaMontada) {
      dispatch(resetMensagensAtendimento());
      dispatch(
        fetchMensagensByAtendimento({
          atendimentoId: atendimentoSelecionado?.id,
          cursor: { afterCursor },
        }),
      )
        .unwrap()
        .then(data => {
          const { cursor } = data;

          dispatch(setCursorAConsumir(cursor));
        });
    }

    setMensagemParaNavegar(null);
  }, [
    mensagemEstaMontada,
    mensagemParaNavegar,
    dispatch,
    atendimentoSelecionado,
  ]);

  useEffect(() => {
    if (!mensagemGrupo || !grupoSelecionado) return;

    const { afterCursor } = mensagemGrupo;

    if (!mensagemEstaMontada) {
      dispatch(resetMensagensGrupos());
      dispatch(
        fetchMensagensByGrupo({
          grupoChatId: grupoSelecionado.id,
          cursor: { afterCursor },
        }),
      )
        .unwrap()
        .then(data => {
          dispatch(setCursorAConsumir({ ...data.cursor, type: 'grupo' }));
        });
    }

    setMensagemGrupo(null);
  }, [mensagemEstaMontada, mensagemGrupo, dispatch, grupoSelecionado]);

  useEffect(() => {
    if (!mensagemHistorico || !historicoAtendimentoSelecionado) return;

    const { afterCursor } = mensagemHistorico;

    if (!mensagemEstaMontada) {
      dispatch(resetMensagensFinalizadas());
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId: historicoAtendimentoSelecionado?.id,
          cursor: { afterCursor },
        }),
      )
        .unwrap()
        .then(data => {
          const { cursor } = data;

          dispatch(setCursorAConsumir({ ...cursor, type: 'historico' }));
        });
    }

    setMensagemHistorico(null);
  }, [
    mensagemEstaMontada,
    mensagemHistorico,
    dispatch,
    historicoAtendimentoSelecionado,
  ]);

  return {
    handleNavigateToMessage,
    handleNavigateToGroupMessage,
    handleNavegarAteMensagemHistorico,
  };
}
